import * as THREE from "three";
import { buildHeadGroup, loadSkinImage } from "./skinModel";

const RENDER_SIZE = 128;

class SkullHeadRenderer {
  private renderer?: THREE.WebGLRenderer;
  private scene?: THREE.Scene;
  private camera?: THREE.OrthographicCamera;
  private readonly cache = new Map<string, string>();
  private readonly pending = new Map<string, Promise<string>>();

  private init(): boolean {
    if (this.renderer) return true;
    try {
      const canvas = document.createElement("canvas");
      canvas.width = RENDER_SIZE;
      canvas.height = RENDER_SIZE;
      this.renderer = new THREE.WebGLRenderer({
        canvas,
        alpha: true,
        antialias: true,
        preserveDrawingBuffer: true,
      });
      this.renderer.setClearColor(0x000000, 0);
      this.scene = new THREE.Scene();
      const half = 8;
      this.camera = new THREE.OrthographicCamera(-half, half, half, -half, 0.1, 100);
      this.camera.position.set(0, 0, 40);
      this.camera.lookAt(0, 0, 0);
      return true;
    } catch {
      this.renderer = undefined;
      return false;
    }
  }

  getCached(hash: string): string | undefined {
    return this.cache.get(hash);
  }

  renderHead(skinUrl: string, hash: string): Promise<string> {
    const cached = this.cache.get(hash);
    if (cached) return Promise.resolve(cached);

    let job = this.pending.get(hash);
    if (!job) {
      job = this.render(skinUrl, hash).finally(() => this.pending.delete(hash));
      this.pending.set(hash, job);
    }
    return job;
  }

  private async render(skinUrl: string, hash: string): Promise<string> {
    if (!this.init()) throw new Error("WebGL unavailable");
    const img = await loadSkinImage(skinUrl);

    const head = buildHeadGroup(img);
    head.rotation.y = THREE.MathUtils.degToRad(45);
    head.rotation.x = THREE.MathUtils.degToRad(16);

    this.scene!.add(head);
    this.renderer!.render(this.scene!, this.camera!);
    const dataUrl = this.renderer!.domElement.toDataURL("image/png");
    this.scene!.remove(head);
    disposeGroup(head);

    this.cache.set(hash, dataUrl);
    return dataUrl;
  }
}

function disposeGroup(group: THREE.Object3D): void {
  group.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.geometry.dispose();
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const mat of mats) {
      (mat as THREE.MeshBasicMaterial).map?.dispose();
      mat.dispose();
    }
  });
}

export const skullHeadRenderer = new SkullHeadRenderer();
